<template>
  <div class="notifications-page">
    <h1>{{ t('notifications.title') }}</h1>

    <div class="filters">
      <label for="filterType">{{ t('notifications.filters.label') }}</label>
      <select id="filterType" v-model="filterType" @change="changeFilter()">
        <option value="all">{{ t('notifications.filters.options.all') }}</option>
        <option value="unread">{{ t('notifications.filters.options.unread') }}</option>
        <option value="simulation_completed">{{ t('notifications.filters.options.simulationCompleted') }}</option>
        <option value="simulation_failed">{{ t('notifications.filters.options.simulationFailed') }}</option>
        <option value="system_update">{{ t('notifications.filters.options.systemUpdate') }}</option>
        <option value="system_maintenance">{{ t('notifications.filters.options.systemMaintenance') }}</option>
        <option value="system_alert">{{ t('notifications.filters.options.systemAlert') }}</option>
      </select>
    </div>

    <div class="notifications-list-section">
      <div v-if="isLoading" class="loading-message">
        {{ t('notifications.states.loading') }}
      </div>
      <div v-else-if="error" class="error-message">
        {{ error }}
      </div>
      <div v-else-if="notifications.length === 0" class="no-notifications-message">
        {{ t('notifications.states.empty') }}
      </div>
      <ul v-else class="notifications-list">
        <li v-for="notification in notifications" :key="notification.id" class="notification-item" :class="{ 'unread': notification.status === 'unread' }">
          <div class="notification-header">
            <span class="timestamp">{{ formatTimestamp(notification.timestamp) }}</span>
            <!-- Bind class for unread title directly for reactivity -->
            <span :class="{ 'title': true, 'unread-title': notification.status === 'unread' }">{{ notification.title }}</span>
          </div>
          <p class="preview">{{ notification.preview }}</p>
          <p v-if="notification.fullContent" class="full-content">{{ notification.fullContent }}</p>
          <div class="actions">
            <button v-if="notification.status === 'unread'" @click="markAsRead(notification.id)">{{ t('notifications.actions.markRead') }}</button>
            <button @click="deleteNotification(notification.id)" class="delete-btn">{{ t('notifications.actions.delete') }}</button>
            <a v-if="notification.linkTo" :href="notification.linkTo" target="_blank" rel="noopener noreferrer" class="details-link">{{ t('notifications.actions.viewDetails') }}</a>
          </div>
        </li>
      </ul>
    </div>

    <div class="pagination-controls" v-if="totalPages > 1 && !isLoading && notifications.length > 0">
      <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">{{ t('notifications.pagination.previous') }}</button>
      <span>{{ t('notifications.pagination.summary', { current: currentPage, total: totalPages }) }}</span>
      <button @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">{{ t('notifications.pagination.next') }}</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { getNotifications, markNotificationAsRead as apiMarkAsRead, deleteNotification as apiDeleteNotification } from '@/services/api.js';

const notifications = ref([]);
const isLoading = ref(false);
const error = ref(null); // Store error message string
const currentPage = ref(1);
const totalPages = ref(1);
const filterType = ref('all'); // Default filter
const notificationsPerPage = 10; // Or make this a configurable const
const { t } = useI18n();

const formatTimestamp = (isoString) => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString();
};

const fetchNotifications = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    // Assuming 'mockUser1' for now. In a real app, userId might come from auth state.
    const fetchedData = await getNotifications(currentPage.value, notificationsPerPage, filterType.value);
    notifications.value = fetchedData;

    // New pagination logic:
    if (fetchedData.length < notificationsPerPage) {
      totalPages.value = currentPage.value; // This is the last page
    } else {
      // Potentially more pages, enable "Next" by setting totalPages higher for the template's button logic
      totalPages.value = currentPage.value + 1;
    }

  } catch (err) {
    console.error('Error fetching notifications:', err);
    const reason = err?.message || t('common.unknownError');
    error.value = t('notifications.errors.loadFailed', { reason }); // Use a user-friendly default if err.message is not specific
    notifications.value = []; // Clear notifications on error
  } finally {
    isLoading.value = false;
  }
};

const markAsRead = async (notificationId) => {
  try {
    const response = await apiMarkAsRead(notificationId);
    if (response.success) {
      const index = notifications.value.findIndex(n => n.id === notificationId);
      if (index !== -1) {
        notifications.value[index].status = 'read';
      }
      // Ideally, emit an event to update global unread count (e.g., for bell icon)
      // For now, this only updates the local list.
    } else {
      throw new Error(response.message || 'Failed to mark as read.');
    }
    } catch (err) {
      console.error('Error marking notification as read:', err);
      const reason = err?.message || t('common.unknownError');
      alert(t('notifications.messages.markFailed', { reason }));
    }
  };

const deleteNotification = async (notificationId) => {
  if (!confirm(t('notifications.messages.deleteConfirm'))) return;
  try {
    const response = await apiDeleteNotification(notificationId);
    if (response.success) {
      alert(t('notifications.messages.deleteSuccess'));
      // Re-fetch notifications to update the list and pagination correctly.
      // This handles cases like deleting the last item on a page.
      await fetchNotifications();
    } else {
      throw new Error(response.message || 'Failed to delete notification.');
    }
  } catch (err) {
    console.error('Error deleting notification:', err);
    const reason = err?.message || t('common.unknownError');
    alert(t('notifications.messages.deleteFailed', { reason }));
    error.value = t('notifications.messages.deleteFailed', { reason });
  }
};

const goToPage = (pageNumber) => {
  if (pageNumber >= 1 && pageNumber <= totalPages.value && pageNumber !== currentPage.value) {
    currentPage.value = pageNumber;
    fetchNotifications();
  }
};

const changeFilter = () => { // Removed 'type' param as v-model handles it
  currentPage.value = 1; // Reset to first page on filter change
  fetchNotifications();
};

onMounted(() => {
  fetchNotifications();
});
</script>

<style scoped>
.notifications-page {
  padding: 20px;
  font-family: Arial, sans-serif;
  max-width: 800px;
  margin: 0 auto;
}

h1 {
  text-align: center;
  margin-bottom: 20px;
  color: #333;
}

.filters {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.filters label {
  font-weight: bold;
}

.filters select {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.notifications-list-section {
  margin-bottom: 20px;
}

.loading-message, .no-notifications-message {
  text-align: center;
  padding: 20px;
  color: #777;
}

.error-message {
  text-align: center;
  padding: 20px;
  color: red;
  border: 1px solid red;
  border-radius: 4px;
  background-color: #ffeeee;
}

.notifications-list {
  list-style-type: none;
  padding: 0;
}

.notification-item {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 10px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

/* Applied directly in template for reactivity with :class */
.unread-title {
  font-weight: bold;
  color: #2c3e50 !important; /* Ensure it overrides default title color */
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.timestamp {
  font-size: 0.85em;
  color: #777;
}

.title {
  font-size: 1.1em;
  color: #333;
}

.preview {
  font-size: 0.95em;
  color: #555;
  margin-top: 0;
  margin-bottom: 10px;
}

.full-content {
  font-size: 0.9em;
  color: #666;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #eee;
}

.actions button, .actions .details-link {
  padding: 6px 12px;
  margin-right: 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  text-decoration: none; /* For link */
  display: inline-block; /* For link */
}

.actions button:hover, .actions .details-link:hover {
  opacity: 0.8;
}

.actions .delete-btn {
  background-color: #f44336;
  color: white;
}

.actions button:not(.delete-btn) {
  background-color: #4CAF50;
  color: white;
}

.actions .details-link {
  background-color: #2196F3;
  color: white;
}


.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
}

.pagination-controls button {
  padding: 8px 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #f8f8f8;
  cursor: pointer;
}

.pagination-controls button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
